import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, useUser, SignIn, SignUp } from '../auth/ClerkWrapper';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useApp } from '../context/AppContext';
import ProtectedRoute from '../components/ProtectedRoute';

// Page Components
import Landing from './Landing';
import Dashboard from './Dashboard';
import Workspaces from './Workspaces';
import Pricing from './Pricing';
import Docs from './Docs';
import Privacy from './Privacy';
import Terms from './Terms';
import Settings from './Settings';
import About from './About';
import Blog from './Blog';
import Contact from './Contact';
import API from './API';
import ConfirmationModal from '../components/ui/ConfirmationModal';
import { trackEvent } from '../utils/analytics';

// Sign In Page Component
function SignInPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 p-4">
      <div className="mb-8">
        <Link
          to="/"
          className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          &larr; Back to home
        </Link>
      </div>
      <SignIn
        routing="path"
        path="/sign-in"
        signUpUrl="/sign-up"
        afterSignInUrl="/workspaces"
        afterSignUpUrl="/workspaces"
        appearance={{
          elements: {
            rootBox: 'mx-auto',
            card: 'shadow-xl border border-zinc-100 rounded-2xl',
            footerActionLink: 'text-zinc-900 hover:text-zinc-700 font-medium',
          }
        }}
      />
    </div>
  );
}

// Sign Up Page Component
function SignUpPage() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50 p-4">
      <div className="mb-8">
        <Link
          to="/"
          className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          &larr; Back to home
        </Link>
      </div>
      <SignUp
        routing="path"
        path="/sign-up"
        signInUrl="/sign-in"
        afterSignInUrl="/workspaces"
        afterSignUpUrl="/workspaces"
        appearance={{
          elements: {
            rootBox: 'mx-auto',
            card: 'shadow-xl border border-zinc-100 rounded-2xl',
            footerActionLink: 'text-zinc-900 hover:text-zinc-700 font-medium',
          }
        }}
      />
    </div>
  );
}

// Payment Handler Component - handles payment redirects
function PaymentHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSignedIn, getToken } = useAuth();
  const { setUserUsage } = useApp();

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const addonStatus = searchParams.get('addon');
    const sessionId = searchParams.get('session_id');

    // Handle subscription payment success
    if (paymentStatus === 'success' && sessionId && isSignedIn) {
      const verifyPayment = async () => {
        try {
          const token = await getToken();
          const response = await api.get(`/payments/verify-session?sessionId=${sessionId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (response.data.success) {
            trackEvent('payment_success_subscription', { sessionId: sessionId });
            alert('Your plan has been upgraded successfully!');
            navigate('/settings', { replace: true });
          } else {
            alert('Payment could not be verified. Please contact support if issues persist.');
            navigate('/settings', { replace: true });
          }
        } catch (err) {
          console.error('Error verifying payment:', err);
          alert('Error verifying payment. Please refresh or contact support.');
          navigate('/settings', { replace: true });
        }
      };
      verifyPayment();
    } else if (paymentStatus === 'cancelled') {
      alert('Payment was cancelled.');
      navigate('/pricing', { replace: true });
    }

    // Handle add-on purchase success
    if (addonStatus === 'success' && sessionId && isSignedIn) {
      const verifyAddon = async () => {
        try {
          const studentCount = searchParams.get('students') || '?';
          const token = await getToken();
          const response = await api.get(`/payments/verify-session?sessionId=${sessionId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (response.data.success) {
            trackEvent('payment_success_addon', { sessionId: sessionId, students: studentCount });
            alert(`${studentCount} students have been added to your plan!`);
            navigate('/settings', { replace: true });
          } else {
            alert('Add-on purchase could not be verified. Please contact support if issues persist.');
            navigate('/settings', { replace: true });
          }
        } catch (err) {
          console.error('Error verifying add-on purchase:', err);
          alert('Error verifying add-on purchase. Please refresh or contact support.');
          navigate('/settings', { replace: true });
        }
      };
      verifyAddon();
    } else if (addonStatus === 'cancelled') {
      alert('Add-on purchase was cancelled.');
      navigate('/pricing', { replace: true });
    }
  }, [searchParams, isSignedIn, getToken, navigate, setUserUsage]);

  return null;
}

// Dashboard Wrapper - redirects to workspaces if no active assignment
function DashboardWrapper() {
  const { activeAssignment, updateAssignment, userUsage } = useApp();
  const navigate = useNavigate();

  // Redirect to workspaces if no active assignment
  useEffect(() => {
    if (!activeAssignment) {
      navigate('/workspaces', { replace: true });
    }
  }, [activeAssignment, navigate]);

  if (!activeAssignment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  const handleBack = () => {
    navigate('/workspaces');
  };

  return (
    <Dashboard
      assignment={activeAssignment}
      onUpdateAssignment={updateAssignment}
      userUsage={userUsage}
      onBack={handleBack}
    />
  );
}

// Workspaces Wrapper
function WorkspacesWrapper() {
  const navigate = useNavigate();
  const { user } = useUser();
  const {
    assignments,
    createAssignment,
    deleteAssignment,
    selectAssignment,
    userUsage,
    clearAssignments,
    isLoadingAssignments,
    refreshAssignments,
  } = useApp();
  const { signOut } = useAuth();

  // Confirmation Modal State
  const [confirmation, setConfirmation] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'danger'
  });

  const closeConfirmation = () => {
    setConfirmation(prev => ({ ...prev, isOpen: false }));
  };

  const handleCreateNew = async (config) => {
    const newAssignment = await createAssignment(config);
    navigate('/dashboard');
    return newAssignment;
  };

  const handleSelect = (id) => {
    selectAssignment(id);
    navigate('/dashboard');
  };

  const handleDelete = (id) => {
    setConfirmation({
      isOpen: true,
      title: 'Delete Assignment',
      message: 'Are you sure you want to delete this assignment? This action cannot be undone and all associated submissions will be lost.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteAssignment(id);
          closeConfirmation();
        } catch (error) {
          console.error('Error deleting assignment:', error);
          alert(`Failed to delete assignment: ${error.response?.data?.error || error.message}`);
          closeConfirmation();
        }
      }
    });
  };

  const handleLogout = async () => {
    clearAssignments();
    await signOut();
    navigate('/');
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (user.firstName) return user.firstName;
    if (user.fullName) return user.fullName;
    if (user.primaryEmailAddress) return user.primaryEmailAddress.emailAddress.split('@')[0];
    return 'User';
  };

  return (
    <>
      <Workspaces
        assignments={assignments}
        projects={[]}
        onCreateNew={handleCreateNew}
        onSelect={handleSelect}
        onDelete={handleDelete}
        onLogout={handleLogout}
        userName={getUserDisplayName()}
        userImageUrl={user?.imageUrl}
        userUsage={userUsage}
        isLoading={isLoadingAssignments}
        onRefresh={refreshAssignments}
      />
      <ConfirmationModal
        isOpen={confirmation.isOpen}
        title={confirmation.title}
        message={confirmation.message}
        onConfirm={confirmation.onConfirm}
        onCancel={closeConfirmation}
        variant={confirmation.variant}
        confirmText="Delete"
      />
    </>
  );
}

// Settings Wrapper
function SettingsWrapper() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { clearAssignments } = useApp();

  const handleLogout = async () => {
    clearAssignments();
    await signOut();
    navigate('/');
  };

  return <Settings onLogout={handleLogout} />;
}

// Pricing Wrapper - handles plan selection
function PricingWrapper() {
  const navigate = useNavigate();
  const { isSignedIn, getToken } = useAuth();

  const handlePlanSelect = async (plan, billingInterval = 'monthly') => {
    if (isSignedIn) {
      const paidPlans = ['Mercury', 'Neptune', 'Jupiter', 'Pro'];
      if (paidPlans.includes(plan)) {
        try {
          const token = await getToken();
          trackEvent('checkout_initiated', { plan: plan, billing: billingInterval });
          const response = await api.post('/payments/create-checkout-session', {
            packageType: plan.toLowerCase(),
            billingInterval: billingInterval
          }, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (response.data.url) {
            window.location.href = response.data.url;
          }
        } catch (err) {
          console.error('Payment error:', err);
          alert(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
        }
      } else if (plan === 'Institution') {
        window.location.href = 'mailto:contact@grademind.ai?subject=Institution Plan Inquiry';
      } else if (plan === 'Luna') {
        navigate('/workspaces');
      } else {
        navigate('/workspaces');
      }
    } else {
      navigate('/sign-up');
    }
  };

  return <Pricing onSelectPlan={handlePlanSelect} />;
}

// Main App Component with Routes
function GradeMindApp() {
  return (
    <div className="font-sans antialiased text-zinc-900 bg-white min-h-screen selection:bg-zinc-900 selection:text-white">
      {/* Payment Handler - processes payment URL params */}
      <PaymentHandler />
      
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/about" element={<About />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/api" element={<API />} />
        <Route path="/pricing" element={<PricingWrapper />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        
        {/* Auth Routes - Clerk with path-based routing */}
        <Route path="/sign-in/*" element={<SignInPage />} />
        <Route path="/sign-up/*" element={<SignUpPage />} />
        
        {/* Protected Routes */}
        <Route path="/workspaces" element={
          <ProtectedRoute>
            <WorkspacesWrapper />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardWrapper />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsWrapper />
          </ProtectedRoute>
        } />
        
        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default GradeMindApp;
